import { Tag } from '@atlas/ui-primitives'
import type { Campaign } from '@/domain/campaign.types'
import { CampaignStatusBadge } from './CampaignStatusBadge'

export interface CampaignRowProps {
	campaign: Campaign
	onActivate: (campaignId: string) => void
}

const CELL = 'px-3 py-3 align-middle'

const INTERACTIVE_DESCENDANT_SELECTOR =
	'a, button, input, select, textarea, summary, [role="button"], [role="link"], [contenteditable="true"]'

function isModifiedClick(event: React.MouseEvent): boolean {
	return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
}

function hasRowTextSelection(row: HTMLElement): boolean {
	const selection = window.getSelection()
	if (!selection || selection.isCollapsed) return false
	return (
		(selection.anchorNode !== null && row.contains(selection.anchorNode)) ||
		(selection.focusNode !== null && row.contains(selection.focusNode))
	)
}

/**
 * One Campaign as the Campaigns list page's row: a semantic `<tr>` with one cell
 * per column, valid only inside a `<tbody>`.
 *
 * The Campaign name is the row's only keyboard-focusable navigation control — the
 * row itself carries no role or tab stop — and it is an anchor so it has link
 * semantics and a real destination. The row additionally accepts an unmodified
 * primary-pointer click anywhere on its non-interactive surface as a navigation
 * convenience: a larger pointer target, not a second accessible control.
 * Activation is reported through `onActivate` without navigating: the parent owns
 * navigation. Window, Objective, and Geography render only when present; an absent
 * value leaves its cell empty rather than substituting a placeholder.
 *
 * A card-shaped row for the same Campaign is `CampaignListRow` in
 * `@atlas/ui-domain`. The two deliberately do NOT share HTML — one is a `<tr>` and
 * the other a `<button>` — but they state the same facts, mapped once by
 * `toCampaignListItem`, so they cannot disagree about what a Campaign is.
 */
export function CampaignRow({ campaign, onActivate }: CampaignRowProps) {
	const objective = campaign.classification.tags.find((tag) => tag.axisCode === 'objective')
	const geography = campaign.classification.tags.find((tag) => tag.axisCode === 'geography')

	const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
		if (event.defaultPrevented) return
		if (isModifiedClick(event)) return
		const target = event.target as HTMLElement
		if (target.closest(INTERACTIVE_DESCENDANT_SELECTOR)) return
		if (hasRowTextSelection(event.currentTarget)) return
		onActivate(campaign.campaignId)
	}

	return (
		<tr
			onClick={handleRowClick}
			className="cursor-pointer border-b border-ds-border last:border-b-0 hover:bg-ds-surface"
		>
			<td className={`${CELL} min-w-0`}>
				<a
					href={`/campaigns/${campaign.campaignId}`}
					onClick={(event) => {
						if (isModifiedClick(event)) return
						event.preventDefault()
						onActivate(campaign.campaignId)
					}}
					className="block min-w-0 truncate font-medium text-ds-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border"
				>
					{campaign.name}
				</a>
				<span className="block min-w-0 truncate text-xs text-ds-text-muted">{campaign.campaignCode}</span>
			</td>
			<td className={`${CELL} whitespace-nowrap text-xs text-ds-text-muted`}>{campaign.windowLabel}</td>
			<td className={`${CELL} whitespace-nowrap`}>
				{objective && (
					<Tag variant="secondary" size="sm">
						{objective.label}
					</Tag>
				)}
			</td>
			<td className={`${CELL} whitespace-nowrap`}>
				{geography && (
					<Tag variant="muted" size="sm">
						{geography.label}
					</Tag>
				)}
			</td>
			<td className={`${CELL} whitespace-nowrap`}>
				<CampaignStatusBadge status={campaign.status} />
			</td>
		</tr>
	)
}
